import { useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { salaryGradesApi } from '../plantillaApi';
import { salaryTypeLabel, type SalaryGradeListItem, type SalaryTypeValue } from '../plantillaTypes';
import { SALARY_STEPS } from '../utils/sampleSalaryGrades';

const ID_PREFIX = 'salary-grade-line';

const STEPS = Array.from({ length: SALARY_STEPS }, (_, step) => step);

/**
 * Correct ONE grade's line of a tranche — its monthly rate at every step it holds, in one pass
 * (product call, 16 Oct 2026: "the edit should be per line only"). It replaced a menu of eight
 * one-rate edits.
 *
 * It CORRECTS and never reshapes: a step the grade has no rate at is shown disabled, and a step
 * it has cannot be blanked. Adding a rate is the tranche's Add grades; removing one is a delete.
 *
 * **No Autogenerate**, deliberately — the form is FILLED AT MOUNT with the line's own rates, so
 * the primary action already has everything it needs (the `<ResetUserPasswordModal>` exemption).
 */
export function SalaryGradeLineModal({
  open,
  type,
  tranche,
  grade,
  rates,
  onClose,
  onSaved,
}: {
  open: boolean;
  type: SalaryTypeValue;
  tranche: number;
  grade: number;
  /** By position: index 0 is step 1. Null where the grade has no rate. */
  rates: (SalaryGradeListItem | null)[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [values, setValues] = useState<string[]>(() => rates.map((rate) => (rate ? String(rate.monthlyAmount) : '')));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const toast = useToast();

  /*
    Why each field is wrong, before submit: a held step left blank, an amount out of range, or a
    step not higher than the nearest filled one to its left. The grade above and below are the
    server's to check — it holds them.
  */
  const problems = useMemo(() => {
    const found = new Map<number, string>();
    let previous: { step: number; amount: number } | null = null;

    for (const step of STEPS) {
      if (!rates[step]) continue;

      const raw = values[step].trim();
      const amount = Number(raw);

      if (raw === '') {
        found.set(step, `Enter the rate at step ${step + 1}.`);
        continue;
      }

      if (!Number.isFinite(amount) || amount < 0 || amount > 10_000_000) {
        found.set(step, 'Enter an amount up to 10,000,000.');
        continue;
      }

      if (previous !== null && amount <= previous.amount) {
        found.set(step, `Step ${step + 1} must be higher than step ${previous.step + 1}.`);
      }

      previous = { step, amount };
    }

    return found;
  }, [rates, values]);

  const canSave = problems.size === 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await salaryGradesApi.updateGradeLine(type, tranche, grade, {
        stepRates: STEPS.map((step) => (rates[step] ? Number(values[step]) : null)),
      });

      toast.success(`SG ${grade} updated.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the rates.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit SG ${grade}`}
      size="md"
      id="salary-grade-line-modal"
      confirm={{
        id: 'salary-grade-line-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="salary-grade-line-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="salary-grade-line-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The line's place in the schedule, stated once — read-only facts. */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Type', value: salaryTypeLabel(type) },
            { label: 'Tranche', value: String(tranche), mono: true },
            { label: 'Grade', value: `SG ${grade}` },
          ]}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {STEPS.map((step) => (
            <TextField
              key={step}
              // `salary-grade-line-step-rates.2` — what the server's `stepRates.2` resolves to.
              id={`${ID_PREFIX}-step-rates.${step}`}
              label={`Step ${step + 1}`}
              required={rates[step] !== null}
              type="number"
              min={0}
              step="0.01"
              description={
                rates[step]
                  ? `The monthly rate for SG ${grade} at step ${step + 1}.`
                  : `SG ${grade} has no rate at step ${step + 1}. Add one from the tranche's Add grades.`
              }
              value={values[step]}
              onChange={(e) => {
                const next = e.target.value;
                setValues((current) => current.map((v, s) => (s === step ? next : v)));
              }}
              error={problems.get(step)}
              disabled={saving || rates[step] === null}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}
